import { authedFetch, getJson } from 'src/components/star-grid/services/starGridHttp';
import { QUERY_ANALYSIS_MAX_AGE_MS, QUERY_STATS_RETRY_DELAYS_MS } from 'src/components/star-grid/queryStatsConstants';
import type { PnlQueryAnalysis, PnlQueryStats } from 'src/components/star-grid/queryStats.types';
import { parseQueryStats } from 'src/components/star-grid/utils/queryStatsSchema';
import { queryStatsDelay } from 'src/components/star-grid/utils/queryStatsDelay';

const readQueryStats = async (queryId: string, reportId: number, signal?: AbortSignal, sourceTable?: string): Promise<PnlQueryStats | null> => {
  try {
    // `source_table` states which fact was scanned. Without it the endpoint infers the source from the
    // report row's `source_mode`, which names the native fact even when the query read Iceberg.
    const source = sourceTable ? `&source_table=${encodeURIComponent(sourceTable)}` : '';
    const response = await authedFetch(`/pnl-reports/query-stats/${encodeURIComponent(queryId)}?report_id=${reportId}${source}`, {
      method: 'GET',
      signal
    });
    if (!response.ok) return null;
    const stats = await parseQueryStats(await response.json());
    return stats.query_id === queryId ? stats : null;
  } catch {
    // Profiles are optional; their failure must never fail the report itself.
    return null;
  }
};
/** A finished query's profile never changes, so reopening the stats panel reads it from here. */
const finishedStats = new Map<string, PnlQueryStats>();

/** Drops the remembered profiles; for tests, which reuse one query id. */
export const forgetFinishedQueryStats = (): void => finishedStats.clear();

export const loadQueryStats = async (
  queryId: string,
  reportId: number,
  signal?: AbortSignal,
  onUpdate?: (stats: PnlQueryStats) => void,
  sourceTable?: string
): Promise<PnlQueryStats | null> => {
  if (!queryId || !Number.isSafeInteger(reportId) || reportId <= 0 || signal?.aborted) return null;
  const known = finishedStats.get(queryId);
  if (known) {
    onUpdate?.(known);
    return known;
  }
  let latest: PnlQueryStats | null = null;
  for (const delayMs of QUERY_STATS_RETRY_DELAYS_MS) {
    if (delayMs && !(await queryStatsDelay(delayMs, signal))) return null;
    if (signal?.aborted) return null;
    const stats = await readQueryStats(queryId, reportId, signal, sourceTable);
    if (signal?.aborted) return null;
    if (!stats) return latest;
    latest = stats;
    onUpdate?.(stats);
    if (stats.available) {
      finishedStats.set(queryId, stats);
      return stats;
    }
  }
  return latest;
};

/** A finished query's analysis; immutable, so one read per query id serves every reopen. */
export const loadQueryAnalysis = (queryId: string, signal?: AbortSignal): Promise<PnlQueryAnalysis> =>
  getJson<PnlQueryAnalysis>(`/pnl-reports/query-stats/${encodeURIComponent(queryId)}/analysis`, signal, QUERY_ANALYSIS_MAX_AGE_MS);
